import { NumericInput } from '@playcanvas/pcui';

const SLIDER_ACTIVE_CLASS = 'pcui-numeric-input-slider-control-active';

type ScrubInstance = NumericInput & {
    _sliderControl?: { dom: HTMLElement; class: { add: (c: string) => void; remove: (c: string) => void } };
    _sliderUsed: boolean;
    _sliderMovement: number;
    _sliderPrevValue: number;
    _updatePosition: (movement: number, shiftKey: boolean) => void;
    _isScrolling: () => boolean;
    _onSliderMouseDown: (evt: MouseEvent) => void;
    _onSliderMouseUp: () => void;
    _volugraphScrubAttached?: boolean;
    _volugraphFallbackActive?: boolean;
    _volugraphFallbackMove?: (evt: MouseEvent) => void;
    _volugraphFallbackWheel?: (evt: WheelEvent) => void;
    _volugraphFallbackUp?: () => void;
    _volugraphFallbackLockListener?: () => void;
    _volugraphFallbackLockTimer?: ReturnType<typeof setTimeout>;
};

function clearFallbackScrub(instance: ScrubInstance) {
    if (instance._volugraphFallbackLockTimer) {
        clearTimeout(instance._volugraphFallbackLockTimer);
        instance._volugraphFallbackLockTimer = undefined;
    }
    if (instance._volugraphFallbackLockListener) {
        document.removeEventListener('pointerlockchange', instance._volugraphFallbackLockListener);
        instance._volugraphFallbackLockListener = undefined;
    }
    if (!instance._volugraphFallbackActive) {
        return;
    }
    if (instance._volugraphFallbackMove) {
        document.removeEventListener('mousemove', instance._volugraphFallbackMove);
        instance._volugraphFallbackMove = undefined;
    }
    if (instance._volugraphFallbackUp) {
        document.removeEventListener('mouseup', instance._volugraphFallbackUp);
        instance._volugraphFallbackUp = undefined;
    }
    if (instance._volugraphFallbackWheel && instance._sliderControl) {
        instance._sliderControl.dom.removeEventListener('wheel', instance._volugraphFallbackWheel);
        instance._volugraphFallbackWheel = undefined;
    }
    instance._sliderControl?.class.remove(SLIDER_ACTIVE_CLASS);
    instance._volugraphFallbackActive = false;
}

function startFallbackScrub(instance: ScrubInstance, evt: MouseEvent) {
    if (!instance._sliderControl || instance._volugraphFallbackActive || instance._isScrolling()) {
        return;
    }

    instance._volugraphFallbackActive = true;
    let lastX = evt.clientX;

    instance._volugraphFallbackMove = (ev: MouseEvent) => {
        const dx = ev.movementX !== 0 ? ev.movementX : ev.clientX - lastX;
        if (ev.movementX === 0) {
            lastX = ev.clientX;
        }
        instance._updatePosition(dx, ev.shiftKey);
    };

    instance._volugraphFallbackWheel = (ev: WheelEvent) => {
        instance._updatePosition(ev.deltaY, ev.shiftKey);
    };

    instance._volugraphFallbackUp = () => {
        clearFallbackScrub(instance);
        instance._onSliderMouseUp();
    };

    instance._sliderControl.class.add(SLIDER_ACTIVE_CLASS);
    document.addEventListener('mousemove', instance._volugraphFallbackMove);
    document.addEventListener('mouseup', instance._volugraphFallbackUp);
    instance._sliderControl.dom.addEventListener('wheel', instance._volugraphFallbackWheel, { passive: true });
}

/**
 * PCUI NumericInput drag scrub uses pointer lock, which embedded Shopify iframes block.
 * When lock fails, fall back to document-level mousemove scrubbing (same step math).
 */
export function attachNumericInputScrubFallback(input: NumericInput): void {
    const instance = input as ScrubInstance;
    if (instance._volugraphScrubAttached || !instance._sliderControl) {
        return;
    }
    instance._volugraphScrubAttached = true;

    const origMouseDown = instance._onSliderMouseDown.bind(instance);
    const origMouseUp = instance._onSliderMouseUp.bind(instance);
    const origDestroy = input.destroy.bind(input);

    instance._onSliderMouseDown = (evt: MouseEvent) => {
        origMouseDown(evt);

        const tryFallback = () => {
            if (instance._isScrolling() || instance._volugraphFallbackActive) {
                return;
            }
            startFallbackScrub(instance, evt);
        };

        instance._volugraphFallbackLockListener = () => {
            if (instance._volugraphFallbackLockTimer) {
                clearTimeout(instance._volugraphFallbackLockTimer);
                instance._volugraphFallbackLockTimer = undefined;
            }
            if (instance._volugraphFallbackLockListener) {
                document.removeEventListener('pointerlockchange', instance._volugraphFallbackLockListener);
                instance._volugraphFallbackLockListener = undefined;
            }
            tryFallback();
        };
        document.addEventListener('pointerlockchange', instance._volugraphFallbackLockListener);

        // Pointer lock may be denied in iframes without firing pointerlockchange.
        instance._volugraphFallbackLockTimer = setTimeout(tryFallback, 50);
    };

    instance._onSliderMouseUp = () => {
        clearFallbackScrub(instance);
        origMouseUp();
    };

    input.destroy = () => {
        clearFallbackScrub(instance);
        origDestroy();
    };
}
